import React, { useMemo, useRef, useState } from "react";
import { getRecommendedVideos, getRecommendationTitle } from "../utils/recommendations";
import VideoCard from "./VideoCard";
import ShimmerVideoCard from "../shared/ShimmerVideoCard";

// "Recommended for you" shelf shown at the top of the home page.
// Personalization is 100% local (localStorage) — no server involved.
// - Returning user  → videos matching their most-viewed topics.
// - New user        → random videos (falls back inside getRecommendedVideos).
const RecommendedRow = ({ videos = [], loading = false }) => {
  const rowRef = useRef(null);
  const [title] = useState(() => {
    try {
      return getRecommendationTitle();
    } catch (e) {
      return "Picks for you";
    }
  });

  const recommended = useMemo(() => {
    if (loading) return [];
    return getRecommendedVideos(videos, 12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, loading]);

  const scroll = (dir) => {
    const el = rowRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.9) || 300;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  const showShimmer = loading || !Array.isArray(videos) || videos.length === 0;

  return (
    <div className="px-5 pb-4 pt-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-black dark:text-white">{title}</h2>
        <button
          type="button"
          onClick={() => scroll("right")}
          className="hidden md:flex items-center text-sm font-semibold text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white"
        >
          See all
          <svg className="ml-1" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="relative -mx-2">
        <div
          ref={rowRef}
          className="overflow-x-auto -mx-2 px-2 scroll-smooth snap-x snap-mandatory show-scrollbar"
          aria-label={title}
        >
          <div className="flex space-x-4">
            {showShimmer
              ? Array(4)
                  .fill("")
                  .map((_, idx) => (
                    <div key={`rec-shimmer-${idx}`} className="flex-shrink-0 w-60 md:w-72 px-2 snap-start">
                      <ShimmerVideoCard />
                    </div>
                  ))
              : recommended.map((video, i) => (
                  <div key={video?.videoId || `rec-${i}`} className="flex-shrink-0 w-60 md:w-72 px-2 snap-start">
                    <VideoCard video={video} />
                  </div>
                ))}
          </div>
        </div>

        {/* arrows - visible on md+ */}
        <button
          type="button"
          onClick={() => scroll("left")}
          className="hidden md:flex items-center justify-center absolute left-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 dark:bg-black/60 shadow-md hover:bg-white dark:hover:bg-black"
          aria-label="Scroll left"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <button
          type="button"
          onClick={() => scroll("right")}
          className="hidden md:flex items-center justify-center absolute right-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 dark:bg-black/60 shadow-md hover:bg-white dark:hover:bg-black"
          aria-label="Scroll right"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default RecommendedRow;
